import { EditNetworkQuestionsSettings, NetworkQuestionsEnablementMode } from '@ms/yammer-graphql';
import mutationEditNetworkQuestionsSettings from '@ms/yammer-graphql/dist/operations/mutationEditNetworkQuestionsSettings.graphql';

import { ManagedApiLifecycle, getGraphqlApiRequest, managedApiAction } from '../..';
import { reportError } from '../../telemetry';
import { PromiseThunkAction } from '../../types';

import {
  EditNetworkQuestionSettingRejectedPayload,
  EditNetworkQuestionSettingsFulfilledAction,
  EditNetworkQuestionSettingsPendingAction,
  EditNetworkQuestionSettingsRejectedAction,
} from './editNetworkQuestionSettingsActions.types';
import { getNetworkQuestionsEnablementMode } from './selectors';

interface NetworkQuestionSettingsResponse {
  readonly data: EditNetworkQuestionsSettings;
}

export enum NetworkQuestionSettingsKeys {
  networkQuestionsEnablementMode = 'networkQuestionsEnablementMode',
}

const editNetworkQuestionSettingsPending = (
  networkQuestionsMode: NetworkQuestionsEnablementMode,
  isNetworkQuestionsAIFeaturesEnabled?: boolean
): EditNetworkQuestionSettingsPendingAction => ({
  type: 'NETWORK_QUESTION_SETTINGS.EDIT_PENDING',
  networkQuestionsMode,
  isNetworkQuestionsAIFeaturesEnabled,
});

type EditNetworkQuestionSettingsFulfilled = (
  payload: EditNetworkQuestionSettingsFulfilledAction['payload'],
  networkQuestionsMode: NetworkQuestionsEnablementMode,
  isNetworkQuestionsAIFeaturesEnabled?: boolean
) => EditNetworkQuestionSettingsFulfilledAction;
const editNetworkQuestionSettingsFulfilled: EditNetworkQuestionSettingsFulfilled = (
  payload,
  networkQuestionsMode,
  isNetworkQuestionsAIFeaturesEnabled
): EditNetworkQuestionSettingsFulfilledAction => ({
  type: 'NETWORK_QUESTION_SETTINGS.EDIT_FULFILLED',
  payload,
  networkQuestionsMode,
  isNetworkQuestionsAIFeaturesEnabled,
});

const editNetworkQuestionSettingsRejected = (
  payload: EditNetworkQuestionSettingRejectedPayload
): EditNetworkQuestionSettingsRejectedAction => ({
  type: 'NETWORK_QUESTION_SETTINGS.EDIT_REJECTED',
  payload,
});

const editNetworkQuestionSettingsRequest = (
  networkQuestionMode: NetworkQuestionsEnablementMode,
  isNetworkQuestionsAIFeaturesEnabled?: boolean
) => {
  const input =
    isNetworkQuestionsAIFeaturesEnabled !== undefined
      ? {
          isNetworkQuestionsAIFeaturesEnabled,
          networkQuestionsEnablementMode: networkQuestionMode,
        }
      : {
          networkQuestionsEnablementMode: networkQuestionMode,
        };

  return getGraphqlApiRequest({
    body: {
      operationName: 'EditNetworkQuestionsSettings',
      query: mutationEditNetworkQuestionsSettings,
      variables: {
        input,
      },
    },
  });
};

const editNetworkQuestionSettingsLifecycle = (
  networkQuestionsMode: NetworkQuestionsEnablementMode,
  prevSettingValue: NetworkQuestionsEnablementMode,
  isNetworkQuestionsAIFeaturesEnabled?: boolean
): ManagedApiLifecycle<NetworkQuestionSettingsResponse> => ({
  pending: () => editNetworkQuestionSettingsPending(networkQuestionsMode, isNetworkQuestionsAIFeaturesEnabled),
  fulfilled: (rawNetworkQuestionSettings) =>
    editNetworkQuestionSettingsFulfilled(
      rawNetworkQuestionSettings.data,
      networkQuestionsMode,
      isNetworkQuestionsAIFeaturesEnabled
    ),
  rejected: () => editNetworkQuestionSettingsRejected({ prevSettingValue }),
});

export type EditNetworkQuestionSettings = (
  networkQuestionsMode: NetworkQuestionsEnablementMode,
  isNetworkQuestionsAIFeaturesEnabled?: boolean
) => PromiseThunkAction<void>;
export const editNetworkQuestionSettings: EditNetworkQuestionSettings =
  (networkQuestionsMode, isNetworkQuestionsAIFeaturesEnabled) => async (dispatch, getState) => {
    const state = getState();
    const previousSettingValue = getNetworkQuestionsEnablementMode(state) || NetworkQuestionsEnablementMode.DISABLED;
    const request = editNetworkQuestionSettingsRequest(networkQuestionsMode, isNetworkQuestionsAIFeaturesEnabled);
    const lifecycle = editNetworkQuestionSettingsLifecycle(
      networkQuestionsMode,
      previousSettingValue,
      isNetworkQuestionsAIFeaturesEnabled
    );
    const action = managedApiAction({ request, lifecycle });

    try {
      await dispatch(action);
    } catch (error) {
      reportError({ error });
    }
  };
